import * as React from 'react';
import { Box, Tab, Tabs as MuiTabs, useMediaQuery } from '@mui/material';
import { SideNavCtx } from 'components/Layouts/constants';
import Theme from 'theme';
import { useHttp } from 'hooks/use-fetch';
import { UserRole } from 'types/NavTreeModel';


const styles = {
  position: 'relative',
  transition: 'top 0.3s',
};

const stickyStyles = {
  position: 'fixed',
  top: Theme.custom.appBarHeight,
  marginTop: 0.75,
  zIndex: 1000,
  backgroundColor: Theme.custom.backgroundColor,
  width: Theme.custom.width,
  maxWidth: Theme.custom.appMaxWidth,
};

export type TabContext = {
  header: string;
  headerIcon?: React.ReactElement;
  content?: React.ReactNode;
};

interface TabsProps {
  tabContext: TabContext[];
  value: number | string;
  variant?: 'standard' | 'fullWidth';
  activeStyles?: React.CSSProperties;
  height?: number;
  setValue: (value: number | string) => void;
  selectedColor?: string;
  unselectedColor?: string;
}

export function Tabs({
  tabContext,
  value,
  variant = 'fullWidth',
  activeStyles,
  height = 42,
  setValue,
  selectedColor,
  unselectedColor,
}: TabsProps) {
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ width: variant === 'fullWidth' ? '100%' : 'auto' }}>
      <MuiTabs
        value={value}
        onChange={handleChange}
        TabIndicatorProps={activeStyles ? { sx: { display: 'none' } } : {}}
        sx={{
          minHeight: height,
          borderRadius: 1,
          bgcolor: Theme.custom.colors.backgroundColorSecondary,
          paddingLeft: '1%',
          '& .MuiTabs-flexContainer': {
            height: '100%',
            alignItems: 'center',
          },
          flexShrink: 0,
        }}
      >
        {tabContext.map((tabs, index) => (
          <Tab
            icon={tabs.headerIcon}
            label={tabs.header}
            iconPosition='start'
            key={`${tabs.header.toLowerCase()}`}
            sx={{
              marginX: 2,
              paddingX: 1,
              minHeight: 30,
              height: '100%',
              ...(index === value ? activeStyles ?? {} : {}),
              color: unselectedColor || undefined,
              '&.Mui-selected': {
                color: selectedColor ? Theme.custom.colors.lightTextPrimary : undefined,
              },
            }}
          />
        ))}
      </MuiTabs>
    </Box>
  );
}

interface TabComponentProps {
  tabContext: TabContext[];
  tabsHeight?: number;
  stickHeader?: boolean;
  defaultTab?: number;
  renderBelowHeader?: (selectedTab: number) => React.ReactElement | null;
  onTabChange?: (selectedTab: number) => void;
  isDemoUser?: boolean;
}

export default function TabComponent({
  tabContext,
  tabsHeight = 42,
  stickHeader = false,
  defaultTab = 0,
  renderBelowHeader,
  onTabChange,
  isDemoUser = false,
}: TabComponentProps) {
  const { userRoles } = useHttp();
  const isChiefAdmin = !!userRoles[UserRole.CHIEF_ADMIN];
  const { scrollPosition } = React.useContext(SideNavCtx);

  const [value, setValue] = React.useState(0);
  const [distanceFromTop, setDistanceFromTop] = React.useState(0);

  const cardRef = React.useRef<HTMLDivElement | null>(null);
  const boxRef = React.useRef<HTMLDivElement | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (cardRef.current) {
      const { top } = cardRef.current.getBoundingClientRect();
      setDistanceFromTop(top);
    }
  }, []);

  const isSticky = React.useMemo(
    () => {
      const offset = isDemoUser ? 2 * tabsHeight : tabsHeight;
      return ((scrollPosition || -Infinity) > distanceFromTop - 18 - offset) && stickHeader;
    },
    [scrollPosition, distanceFromTop, tabsHeight, stickHeader, isDemoUser],
  );

  const offset = boxRef?.current?.offsetHeight ?? 0;
  const pt = isSticky ? `${offset}px` : 0;

  const scrollIntoView = React.useCallback(() => {
    if (isSticky) {
      setTimeout(() => containerRef?.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [isSticky]);

  stickyStyles.top = `calc(${Theme.custom.appBarHeight} + ${!isChiefAdmin ? Theme.custom.navBarHeight : '0px'} + ${
    isDemoUser ? Theme.custom.demoBarHeight : '0px'
  })`;

  const onTabChangeHandler = (selectedTab: number) => {
    if (onTabChange) {
      onTabChange(selectedTab);
    }
    scrollIntoView();
    setValue(selectedTab);
  };

  React.useEffect(
    () => {
      if (defaultTab >= 0) {
        setValue((prev) => (prev !== defaultTab ? defaultTab : prev));
      }
    },
    [defaultTab],
  );

  const isSmallScreen = useMediaQuery('(max-width:1500px)');

  return (
    <Box display='flex' flexDirection='column' overflow='auto'>
      <Box ref={boxRef} sx={isSticky ? { ...styles, ...stickyStyles } : styles}>
        <Box
          ref={cardRef}
          borderRadius={3}
          px={isSmallScreen ? 8 : 6}
          maxWidth={1500}
          sx={{
            backgroundColor: Theme.custom.colors.backgroundColorSecondary,
            borderBottom: '1px solid #0000001F',
          }}
        >
          <Box
            display='flex'
            alignItems='center'
            sx={{
              overflowX: 'auto',
              width: '100%',
              whiteSpace: 'nowrap',
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <Tabs
                tabContext={tabContext}
                value={value}
                setValue={(v) => onTabChangeHandler(v as number)}
                height={tabsHeight}
                unselectedColor='#4C4C4C'
                variant='standard'
              />
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            {renderBelowHeader && (
            <Box
              display='flex'
              alignItems='center'
              gap={2}
              sx={{
                overflowX: 'auto',
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              {renderBelowHeader(value)}
            </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box pt={pt} ref={containerRef} display='flex' flexDirection='column' overflow='auto' px={isSmallScreen ? 8 : 6}>
        {tabContext.map((tabs, index) => (
          value === index ? <Box key={tabs.header} display='flex' flexDirection='column' overflow='auto'>{tabs.content}</Box> : null))}
      </Box>
    </Box>
  );
}
